// Derived from connect-rtc-js rtp-stats.js for consistency
// https://github.com/aws/connect-rtc-js/blob/HEAD/src/js/rtp-stats.js

type StreamType = 'audio_input' | 'audio_output' | 'video_input' | 'video_output';

type ExtractedStats = {
  timestamp: Date;
  packetsCount?: number | null;
  packetsLost?: number | null;
  audioLevel?: number | null;
  procMilliseconds?: number | null;
  rttMilliseconds?: number | null;
  jbMilliseconds?: number | null;
  bytesSent?: number | null;
  bytesReceived?: number | null;
  framesEncoded?: number | null;
  framesDecoded?: number | null;
  frameRateSent?: number | null;
  frameRateReceived?: number | null;
};

const isDefined = (v: unknown): boolean => typeof v !== 'undefined';

const whenDefined = (v: unknown, alternative: unknown = null): unknown => (isDefined(v) ? v : alternative);

/**
 * Extract rtp stats of specified stream from RTCStatsReport.
 * Chrome reports all stream stats in statsReports whereas firefox reports only single stream stats in report.
 */
export function extractMediaStatsFromStats(
  timestamp: Date,
  stats: RTCStatsReport,
  streamType: StreamType,
): MediaRtpStats | null {
  let extractedStats: ExtractedStats | null = null;
  let reportType: string | null = null;
  let packetsSent: number | null = null;

  stats.forEach((statsReport: any) => {
    if (!statsReport) return;

    if (statsReport.type === 'ssrc') {
      reportType = statsReport.type;

      if (
        parseInt(statsReport.stat('packetsSent')) &&
        statsReport.stat('mediaType') === 'audio' &&
        streamType === 'audio_output'
      ) {
        extractedStats = {
          timestamp,
          packetsCount: parseInt(statsReport.stat('packetsSent')),
          bytesSent: parseInt(statsReport.stat('bytesSent')),
          audioLevel: whenDefined(parseInt(statsReport.stat('audioInputLevel'))) as number | null,
          packetsLost: isDefined(statsReport.stat('packetsLost')) ? Math.max(0, statsReport.stat('packetsLost')) : 0,
          procMilliseconds: whenDefined(parseInt(statsReport.stat('googCurrentDelayMs'))) as number | null,
          rttMilliseconds: whenDefined(parseInt(statsReport.stat('googRtt'))) as number | null,
          jbMilliseconds: whenDefined(parseInt(statsReport.stat('googJitterReceived'))) as number | null,
        };
      } else if (
        parseInt(statsReport.stat('packetsReceived')) &&
        statsReport.stat('mediaType') === 'audio' &&
        streamType === 'audio_input'
      ) {
        extractedStats = {
          timestamp,
          packetsCount: parseInt(statsReport.stat('packetsReceived')),
          bytesReceived: parseInt(statsReport.stat('bytesReceived')),
          audioLevel: whenDefined(parseInt(statsReport.stat('audioOutputLevel'))) as number | null,
          packetsLost: isDefined(parseInt(statsReport.stat('packetsLost')))
            ? Math.max(0, statsReport.stat('packetsLost'))
            : 0,
          procMilliseconds: whenDefined(parseInt(statsReport.stat('googCurrentDelayMs'))) as number | null,
          jbMilliseconds: whenDefined(parseInt(statsReport.stat('googJitterReceived'))) as number | null,
        };
      }
    } else if (
      statsReport.type === 'inbound-rtp' &&
      !statsReport.isRemote &&
      (streamType === 'audio_input' || streamType === 'video_input')
    ) {
      reportType = statsReport.type;
      extractedStats = {
        timestamp,
        packetsLost: statsReport.packetsLost,
        packetsCount: statsReport.packetsReceived,
        jbMilliseconds: ((whenDefined(statsReport.jitter, 0) as number) ?? 0) * 1000,
      };
    } else if (
      statsReport.type === 'outbound-rtp' &&
      !statsReport.isRemote &&
      (streamType === 'audio_output' || streamType === 'video_output')
    ) {
      if (extractedStats && !extractedStats.packetsCount) {
        extractedStats.packetsCount = statsReport.packetsSent;
      } else {
        packetsSent = statsReport.packetsSent;
      }
    } else if (
      statsReport.type === 'remote-inbound-rtp' &&
      (streamType === 'audio_output' || streamType === 'video_output')
    ) {
      reportType = statsReport.type;
      extractedStats = {
        timestamp,
        packetsLost: statsReport.packetsLost,
        packetsCount: packetsSent,
        rttMilliseconds: Number.isInteger(statsReport.roundTripTime)
          ? statsReport.roundTripTime
          : isDefined(statsReport.roundTripTime)
          ? statsReport.roundTripTime * 1000
          : null,
        jbMilliseconds: ((whenDefined(statsReport.jitter, 0) as number) ?? 0) * 1000,
      };
    } else if (
      statsReport.type === 'inbound-rtp' &&
      statsReport.isRemote &&
      (streamType === 'audio_output' || streamType === 'video_output')
    ) {
      reportType = statsReport.type;
      extractedStats = {
        timestamp,
        packetsLost: statsReport.packetsLost,
        packetsCount: packetsSent,
        rttMilliseconds: Number.isInteger(statsReport.roundTripTime)
          ? statsReport.roundTripTime
          : isDefined(statsReport.roundTripTime)
          ? statsReport.roundTripTime * 1000
          : null,
        jbMilliseconds: ((whenDefined(statsReport.jitter, 0) as number) ?? 0) * 1000,
      };
    }
  });

  return extractedStats ? new MediaRtpStats(extractedStats, reportType, streamType) : null;
}

export class MediaRtpStats {
  private _timestamp: number;
  private _packetsLost: number | null;
  private _packetsCount: number | null;
  private _audioLevel: number | null;
  private _procMilliseconds: number | null;
  private _rttMilliseconds: number | null;
  private _jbMilliseconds: number | null;
  private _bytesSent: number | null;
  private _bytesReceived: number | null;
  private _framesEncoded: number | null;
  private _framesDecoded: number | null;
  private _frameRateSent: number | null;
  private _frameRateReceived: number | null;
  private _statsReportType: string;
  private _streamType: string;

  constructor(params: ExtractedStats, statsReportType: string | null, streamType: string) {
    this._timestamp = params.timestamp?.getTime() ?? new Date().getTime();
    this._packetsLost = (whenDefined(params.packetsLost) as number | null) ?? null;
    this._packetsCount = (whenDefined(params.packetsCount) as number | null) ?? null;
    this._audioLevel = (whenDefined(params.audioLevel) as number | null) ?? null;
    this._procMilliseconds = (whenDefined(params.procMilliseconds) as number | null) ?? null;
    this._rttMilliseconds = (whenDefined(params.rttMilliseconds) as number | null) ?? null;
    this._jbMilliseconds = (whenDefined(params.jbMilliseconds) as number | null) ?? null;
    this._bytesSent = (whenDefined(params.bytesSent) as number | null) ?? null;
    this._bytesReceived = (whenDefined(params.bytesReceived) as number | null) ?? null;
    this._framesEncoded = (whenDefined(params.framesEncoded) as number | null) ?? null;
    this._framesDecoded = (whenDefined(params.framesDecoded) as number | null) ?? null;
    this._frameRateSent = (whenDefined(params.frameRateSent) as number | null) ?? null;
    this._frameRateReceived = (whenDefined(params.frameRateReceived) as number | null) ?? null;
    this._statsReportType = statsReportType ?? 'unknown';
    this._streamType = streamType ?? 'unknown';
  }

  get timestamp() {
    return this._timestamp;
  }
  get packetsLost() {
    return this._packetsLost;
  }
  get packetsCount() {
    return this._packetsCount;
  }
  get packetLossPercentage() {
    return this._packetsCount && this._packetsCount > 0 ? (this._packetsLost ?? 0) / this._packetsCount : 0;
  }
  get audioLevel() {
    return this._audioLevel;
  }
  get procMilliseconds() {
    return this._procMilliseconds;
  }
  get rttMilliseconds() {
    return this._rttMilliseconds;
  }
  get jbMilliseconds() {
    return this._jbMilliseconds;
  }
  get bytesSent() {
    return this._bytesSent;
  }
  get bytesReceived() {
    return this._bytesReceived;
  }
  get framesEncoded() {
    return this._framesEncoded;
  }
  get framesDecoded() {
    return this._framesDecoded;
  }
  get frameRateSent() {
    return this._frameRateSent;
  }
  get frameRateReceived() {
    return this._frameRateReceived;
  }
  get statsReportType() {
    return this._statsReportType;
  }
  get streamType() {
    return this._streamType;
  }
}
